/*
  The Meta Pixel loader — the one place fbevents.js can enter the page.

  ── Why this file exists ───────────────────────────────────────────────────
  The base code used to sit inline in public/index.html, which meant
  fbevents.js was requested and PageView was sent on every document load,
  before the visitor had been asked anything. Legal requires opt-in first, so
  the script is no longer in the document at all: it is injected from here, and
  only after hasMarketingConsent() is true.

  That ordering is the point. Blocking events after the library has loaded
  would be too late — loading fbevents.js is itself a request to Meta carrying
  the visitor's IP, user agent and referrer, and `fbq("init")` drops the _fbp
  first-party cookie. Neither may happen without consent, so neither is on the
  page until ensureMetaPixel() decides it may be.

  ── The gate ───────────────────────────────────────────────────────────────
  Nothing here reads a flag it was passed. Every entry point asks consent.js
  itself, so there is no route by which a caller can talk a pixel onto the page
  by claiming to have consent.

  ── Revocation ─────────────────────────────────────────────────────────────
  Once fbevents.js is on the page it cannot be unloaded. On a revoke the pixel
  is told `fbq("consent", "revoke")`, which stops it transmitting, and a local
  latch closes so that nothing in this bundle calls fbq again for the rest of
  the page's life. A visitor who revokes and then accepts again is tracked from
  their next document load, not from the middle of this one: re-granting would
  mean reinitialising a pixel that has already been stood down, and a page
  reload is a clean, auditable start. Documented as a limitation in
  CONSENT_INTEGRATION.md.
*/
import { hasMarketingConsent } from "./consent";

/*
  Public by nature. A Pixel ID is visible to anyone who opens Events Manager's
  own network traffic, and it is the only Meta credential the browser gets. The
  Conversions API access token is server-side (server/.env, Secret Manager) and
  has no REACT_APP_ variable, so it cannot reach the bundle.
*/
export const META_PIXEL_ID = "1389170349321859";

const FBEVENTS_SRC = "https://connect.facebook.net/en_US/fbevents.js";

/* Module scope, so StrictMode's double effect, a remount, or two call sites
   racing each other all see the same answer. */
let injected = false;
let revoked = false;

/**
 * Stand up window.fbq the way Meta's snippet does: a synchronous stub that
 * queues calls until the real library arrives and replaces callMethod. Written
 * out rather than pasted minified so the consent gate above it is reviewable.
 */
function installStub() {
  if (typeof window.fbq === "function") return;

  const stub = function (...args) {
    if (stub.callMethod) stub.callMethod.apply(stub, args);
    else stub.queue.push(args);
  };
  stub.push = stub;
  stub.loaded = true;
  stub.version = "2.0";
  stub.queue = [];

  window.fbq = stub;
  if (!window._fbq) window._fbq = stub;
}

function injectScript() {
  const script = document.createElement("script");
  script.async = true;
  script.src = FBEVENTS_SRC;
  const first = document.getElementsByTagName("script")[0];
  if (first?.parentNode) first.parentNode.insertBefore(script, first);
  else document.head.appendChild(script);
}

/**
 * True when this bundle is allowed to call fbq right now: consent is in force
 * and it has not been revoked during this page's life.
 *
 * Every Meta call site goes through this, so a single false here is the whole
 * of "no tracking".
 */
export function isMetaTrackingAllowed() {
  return !revoked && hasMarketingConsent();
}

/**
 * Load and initialise the Pixel if — and only if — marketing consent is in
 * force. Idempotent: the script is injected and `init` is called at most once
 * per page load.
 *
 * Note that `fbq("init", id)` sends no event of its own; PageView is fired
 * explicitly by MetaPixel.jsx, which owns the count.
 *
 * @returns {boolean} whether the Pixel is now usable.
 */
export function ensureMetaPixel() {
  if (!isMetaTrackingAllowed()) return false;
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (injected) return typeof window.fbq === "function";

  injected = true;
  installStub();
  injectScript();
  window.fbq("init", META_PIXEL_ID);
  return true;
}

/**
 * Stop tracking, for a revoke or an explicit reject.
 *
 * A pixel that was never loaded needs no teardown: there is nothing on the
 * page to stand down, and hasMarketingConsent() already refuses everything. So
 * the latch is only closed when a pixel really is running. That distinction is
 * what lets a visitor who clicked Reject and then changed their mind be
 * tracked from the same page load, while a genuine revoke — accepted, pixel
 * loaded, then withdrawn — stays off until the next one.
 *
 * Never reinitialises and never fires an event.
 */
export function disableMetaPixel() {
  if (!injected) return;
  /* Latch first, so anything racing isMetaTrackingAllowed is refused, then
     tell the loaded pixel to stop transmitting on its own account too. */
  revoked = true;
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    try {
      window.fbq("consent", "revoke");
    } catch {
      /* Best effort. The latch above is what actually stops this bundle. */
    }
  }
}

/** Test-only. The latches are module state on purpose, so tests must clear them. */
export function resetMetaPixelForTests() {
  injected = false;
  revoked = false;
}
